import { CurrencyPipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { SharedValidators } from '@shared';
import { CaseInvoicing, CaseName, Fakturalinjer } from '@store/models';
import { AlertService } from 'app/core/alerts/alert.service';
import { ConfirmService } from 'app/shared/confirm-dialog/confirm-dialog.service';
import { localCurrencyToNumber } from 'app/shared/currency';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
    selector: 'app-invoice-record',
    templateUrl: './invoice-record.component.html',
    styleUrls: ['./invoice-record.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class InvoiceRecordComponent implements OnInit, OnChanges, OnDestroy {
    @Input() fakturaLinje: Fakturalinjer;
    @Input() createCases: CaseInvoicing[];
    @Input() caseName: CaseName;
    @Input() edit = false;
    @Input() editAllowed = true;
    @Output() editChange = new EventEmitter<boolean>();
    @Output() delete = new EventEmitter();
    @Output() save = new EventEmitter<Fakturalinjer>();

    form: UntypedFormGroup;
    sagChange$: Subscription;

    @ViewChild('tekst', {static: false}) tekst: ElementRef;

    constructor(
        private fb: UntypedFormBuilder,
        private currencyPipe: CurrencyPipe,
        private alert: AlertService,
        private confirm: ConfirmService
    ) {
        this.createForm();
    }

    ngOnInit() {
        this.sagChange$ = this.form.get('sag').valueChanges.pipe(
            tap(sagId => {
                if (!sagId || !this.edit) { return; }
                const sag = this.createCases.find(s => s.pid === +sagId);
                this.form.patchValue({
                    tekst: sag?.beskrivelse || '',
                    beløb: this.currencyPipe.transform(sag?.ufaktureret || 0, 'DKK', '', '1.2-2', 'da')
                });
                this.tekst?.nativeElement.select();
            })
        ).subscribe();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.fakturaLinje && this.fakturaLinje && !this.edit) {
            this.patchFakturalinjeToForm();
        }
    }

    ngOnDestroy() {
        this.sagChange$.unsubscribe();
    }

    createForm() {
        this.form = this.fb.group({
            flid: 0,
            fid: null,
            tekst: '',
            beløb: [0, SharedValidators.currency],
            sag: [null, Validators.required],
            moms: [25, [Validators.min(0), Validators.max(100)]],
            kontor: null
        });
    }

    patchFakturalinjeToForm() {
        this.form.patchValue({
            ...this.fakturaLinje,
            beløb: this.currencyPipe.transform(this.fakturaLinje.beløb, 'DKK', '', '1.2-2', 'da')
        });
    }

    async onDelete() {
        await this.confirm.question(
            'Er du sikker på du vil slette denne linje?',
            'Slet',
            'Annuller'
        ).then(yesDelete => {
            if (yesDelete) {
                this.delete.emit();
            }
        });
    }

    cancelEdit(flid: number) {
        if (flid === 0) {
            this.delete.emit();
        }
        this.patchFakturalinjeToForm();
        this.onEditChange(false);
    }

    onSave() {
        if (!this.form.valid) {
            this.alert.createAlert('Fejl i fakturalinje!', 'danger');
            return;
        }
        const fakturalinje: Fakturalinjer = {
            ...this.form.value,
            beløb: localCurrencyToNumber(this.form.get('beløb').value)
        };
        this.save.emit(fakturalinje);
        this.onEditChange(false);
    }

    onEditChange(edit: boolean) {
        this.edit = edit;
        this.editChange.emit(edit);
    }

}
